import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { MediaFile } from "../../models/media-file";
import { MediaUsage } from "../../models/media-usage";

/**
 * HTTP client for the in-product Media Manager backend (BPA-backend `/media` endpoints).
 *
 * Day-1 scope is Flavour B (global media): no service filter is applied. JWT is
 * auto-attached by the existing `AuthInterceptor`, so callers don't need to thread
 * the token through.
 *
 * See `docs/media-manager-analysis.md` for the contract and
 * UNCTAD-eRegistrations/BPA-frontend#95 for the architecture spec.
 */
@Injectable()
export class MediaApiService {

  private baseUrl: string = environment.BASE_URL;

  constructor(private http: HttpClient) {
  }

  /** GET /media — list all media files, newest first. */
  public list(): Observable<MediaFile[]> {
    return this.http.get(this.baseUrl + "media") as Observable<MediaFile[]>;
  }

  /** GET /media/{id} — single metadata DTO. */
  public get(id: string): Observable<MediaFile> {
    return this.http.get(this.baseUrl + "media/" + id) as Observable<MediaFile>;
  }

  /**
   * POST /media — multipart upload. Returns the persisted MediaFile DTO.
   * The backend re-detects the MIME server-side (invariant I5) and rejects mismatches
   * with HTTP 415 + body `{declared, detected}`.
   */
  public upload(file: File): Observable<MediaFile> {
    const formData = new FormData();
    formData.append("file", file);
    return this.http.post(this.baseUrl + "media", formData) as Observable<MediaFile>;
  }

  /**
   * PUT /media/{id} — replace content in-place. The id is stable, so any bot
   * referencing this file (`selectedMediaFileId`) keeps resolving (invariant I1).
   */
  public replace(id: string, file: File): Observable<MediaFile> {
    const formData = new FormData();
    formData.append("file", file);
    return this.http.put(this.baseUrl + "media/" + id, formData) as Observable<MediaFile>;
  }

  /**
   * DELETE /media/{id} — remove the metadata row + GridFS binary. Returns 204 on
   * success. Returns 409 with body `{usages: [...]}` if at least one bot references
   * the file (invariant I2). Use the full HTTP response so callers can branch on
   * status code AND read the body.
   */
  public delete(id: string): Observable<HttpResponse<{ usages?: MediaUsage[] }>> {
    return this.http.delete(this.baseUrl + "media/" + id, {
      observe: "response",
    }) as Observable<HttpResponse<{ usages?: MediaUsage[] }>>;
  }

  /** GET /media/{id}/usages — list bots referencing this file. */
  public usages(id: string): Observable<MediaUsage[]> {
    return this.http.get(this.baseUrl + "media/" + id + "/usages") as Observable<MediaUsage[]>;
  }

  /**
   * Build a download URL for the binary endpoint. Used by the upcoming
   * `MediaLibraryComponent` for "preview / download" links and by DS-Frontend's
   * download-action handler. The URL is JWT-protected; the AuthInterceptor will
   * attach the token when fetched via HttpClient.
   */
  public binaryUrl(id: string): string {
    return this.baseUrl + "media/" + id + "/binary";
  }
}
